/**
 * ContactCombobox — searchable contact picker.
 *
 * Replaces plain <select> dropdowns that list every contact (unusable once
 * the contact list grows). Filters by name + email as you type; selecting a
 * match calls onSelect with the contact id and clears the field.
 */

import { useMemo, useRef, useState } from 'react';
import type { FiasLeadsContactDto } from '@/lib/api';

interface Props {
  contacts: FiasLeadsContactDto[];
  onSelect: (contactId: string) => void;
  excludeIds?: string[];
  placeholder?: string;
  disabled?: boolean;
}

function contactLabel(c: FiasLeadsContactDto): string {
  const name = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
  return name || c.primaryEmail || c.contactId;
}

export function ContactCombobox({
  contacts,
  onSelect,
  excludeIds = [],
  placeholder,
  disabled,
}: Props) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | null>(null);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const exclude = new Set(excludeIds);
    return contacts
      .filter((c) => !exclude.has(c.contactId))
      .filter((c) => {
        if (!q) return true;
        const hay =
          `${c.firstName ?? ''} ${c.lastName ?? ''} ${c.primaryEmail ?? ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 50);
  }, [contacts, excludeIds, search]);

  const choose = (c: FiasLeadsContactDto) => {
    onSelect(c.contactId);
    setSearch('');
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={search}
        disabled={disabled}
        placeholder={placeholder ?? 'Search contacts…'}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so an option's onClick lands before the list unmounts.
          blurTimer.current = window.setTimeout(() => setOpen(false), 120);
        }}
        aria-label="Search contacts"
        className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
      />
      {open && matches.length > 0 && (
        <ul
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-border bg-card shadow-lg"
          onMouseDown={() => {
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
          }}
        >
          {matches.map((c) => (
            <li key={c.contactId}>
              <button
                type="button"
                onClick={() => choose(c)}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-muted/40"
              >
                <span className="font-medium">{contactLabel(c)}</span>
                {c.primaryEmail && contactLabel(c) !== c.primaryEmail && (
                  <span className="ml-2 text-xs text-muted-foreground">{c.primaryEmail}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && search.trim() !== '' && matches.length === 0 && (
        <div className="absolute z-10 mt-1 w-full rounded border border-border bg-card px-3 py-2 text-sm text-muted-foreground shadow-lg">
          No contacts match.
        </div>
      )}
    </div>
  );
}
